import type { Task } from '../lib/types'
import { PRIORITY_LABELS, formatDue, isOverdue, priorityMark } from '../lib/format'
import { TaskKey } from './TaskKey'
import { Avatar, EmptyState, TagChip, cx } from './ui'

/**
 * The list view. Same data as the board, arranged for triage rather than for
 * flow — and the only view that works properly on a phone, where a horizontal
 * board of 288px columns does not.
 */
export function TaskList({
  tasks, onOpen, showProject = true, groupBy,
}: {
  tasks: Task[]
  onOpen: (task: Task) => void
  showProject?: boolean
  groupBy?: (task: Task) => string
}) {
  if (tasks.length === 0) {
    return <EmptyState title="Nothing here" hint="No tasks match the current filters." />
  }

  if (!groupBy) {
    return (
      <div className="divide-y divide-[var(--line)]">
        {tasks.map((task) => (
          <Row key={task.id} task={task} onOpen={onOpen} showProject={showProject} />
        ))}
      </div>
    )
  }

  // Insertion order is preserved by Map, so groups appear in the order the
  // caller sorted the tasks into.
  const groups = new Map<string, Task[]>()
  for (const task of tasks) {
    const label = groupBy(task)
    const bucket = groups.get(label)
    if (bucket) bucket.push(task)
    else groups.set(label, [task])
  }

  return (
    <div>
      {[...groups].map(([label, group]) => (
        <section key={label}>
          <h2 className="sticky top-0 z-10 flex items-center gap-2 border-b border-[var(--line)] bg-[var(--bg)]/95 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--ink-3)] backdrop-blur">
            {label}
            <span className="task-key text-[11px] normal-case tracking-normal">{group.length}</span>
          </h2>
          <div className="divide-y divide-[var(--line)]">
            {group.map((task) => (
              <Row key={task.id} task={task} onOpen={onOpen} showProject={showProject} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function Row({
  task, onOpen, showProject,
}: {
  task: Task
  onOpen: (task: Task) => void
  showProject: boolean
}) {
  const overdue = isOverdue(task)
  const mark = priorityMark(task.priority)

  return (
    <button
      onClick={() => onOpen(task)}
      className={cx(
        'flex w-full items-center gap-3 px-4 py-2 text-left transition-colors duration-[110ms]',
        'hover:bg-[var(--sunken)]',
        task.statusDone && 'opacity-65',
      )}
    >
      <TaskKey taskKey={task.key} className="w-[92px] shrink-0 text-[var(--ink-2)]" />

      {mark && (
        <span
          title={PRIORITY_LABELS[task.priority]}
          className="task-key w-3 shrink-0 text-[11px] font-bold text-[var(--danger)]"
        >
          {mark}
        </span>
      )}

      <span className="min-w-0 flex-1 truncate text-[13px]">{task.title}</span>

      <span className="hidden shrink-0 items-center gap-1 sm:flex">
        {task.tags.slice(0, 2).map((tag) => (
          <TagChip key={tag.id} tag={tag} />
        ))}
      </span>

      {showProject && (
        <span className="task-key hidden w-[64px] shrink-0 truncate text-[11px] text-[var(--ink-3)] md:block">
          {task.projectKey}
        </span>
      )}

      <span className="hidden w-[86px] shrink-0 truncate text-[11.5px] text-[var(--ink-3)] sm:block">
        {task.statusName}
      </span>

      <span
        className={cx(
          'task-key w-[62px] shrink-0 text-right text-[11px]',
          overdue ? 'font-semibold text-[var(--danger)]' : 'text-[var(--ink-3)]',
        )}
      >
        {formatDue(task.dueDate)}
      </span>

      <Avatar user={task.assignee} size={20} />
    </button>
  )
}
